import type { OcrFailedReason } from '../../core/utils/ocr-failed-reason.utils';
import type {
  EvidenceFileAttributionApiResponse,
  ProjectEvidenceFileItemApiResponse,
} from '../../features/evidence-files/evidence-files.models';

/**
 * 未分類 attribution のリテラル（Issue #443/#447）。
 * documentType 未割当を表す。全フィールドが null。
 */
const EMPTY_ATTRIBUTION: EvidenceFileAttributionApiResponse = {
  documentTypeCode: null,
  documentTypePublicId: null,
  attributionUnit: null,
  caseId: null,
  employeeId: null,
  targetMonth: null,
};

/**
 * Issue #443/#447: attribution サンプルパターン（5 種）
 *   - employee: 社員紐付け
 *   - case: Case 紐付け
 *   - month: 対象月紐付け
 *   - project: Project 単位（追加値なし）
 *   - 未分類: EMPTY_ATTRIBUTION（documentType 未割当）
 */
const EMPLOYEE_ATTRIBUTION_YAMADA: EvidenceFileAttributionApiResponse = {
  documentTypeCode: 'social_insurance_notification',
  documentTypePublicId: 'doc-type-002',
  attributionUnit: 'employee',
  caseId: null,
  employeeId: 'EMP001',
  targetMonth: null,
};

const MONTH_ATTRIBUTION_APRIL: EvidenceFileAttributionApiResponse = {
  documentTypeCode: 'payslip',
  documentTypePublicId: 'doc-type-004',
  attributionUnit: 'month',
  caseId: null,
  employeeId: null,
  targetMonth: '202504',
};

const CASE_ATTRIBUTION_SATO: EvidenceFileAttributionApiResponse = {
  documentTypeCode: 'work_report',
  documentTypePublicId: 'doc-type-006',
  attributionUnit: 'case',
  caseId: 'case-002',
  employeeId: null,
  targetMonth: null,
};

/**
 * attributionUnit='case' × category='employment' の attribution。
 * matrix の allowedType='employee' (社員行のみ選択可) を Mock UI で対話検証するために追加
 * (Issue #447 Phase B レビュー反映)。
 * 対象 case は EMP001 の雇用審査 case-001。
 */
const CASE_ATTRIBUTION_EMPLOYMENT_YAMADA: EvidenceFileAttributionApiResponse = {
  documentTypeCode: 'employment_case_review',
  documentTypePublicId: 'doc-type-101',
  attributionUnit: 'case',
  caseId: 'case-001',
  employeeId: null,
  targetMonth: null,
};

/**
 * attributionUnit='case' × category='journal_entry' の attribution。
 * matrix の allowedType='journal_entry' (月セルのみ選択可) を Mock UI で対話検証するために追加
 * (Issue #447 Phase B レビュー反映)。
 * 対象 case は EMP001 の仕訳審査 case-001-je-04 (2025年04月)。
 */
const CASE_ATTRIBUTION_JOURNAL_ENTRY_YAMADA: EvidenceFileAttributionApiResponse =
  {
    documentTypeCode: 'journal_entry_case_review',
    documentTypePublicId: 'doc-type-102',
    attributionUnit: 'case',
    caseId: 'case-001-je-04',
    employeeId: null,
    targetMonth: null,
  };

const PROJECT_ATTRIBUTION_ORG_CHART: EvidenceFileAttributionApiResponse = {
  documentTypeCode: 'organization_chart',
  documentTypePublicId: 'doc-type-007',
  attributionUnit: 'project',
  caseId: null,
  employeeId: null,
  targetMonth: null,
};

const PROJECT_ATTRIBUTION_DISPATCH_CONTRACT: EvidenceFileAttributionApiResponse =
  {
    documentTypeCode: 'dispatch_contract',
    documentTypePublicId: 'doc-type-100',
    attributionUnit: 'project',
    caseId: null,
    employeeId: null,
    targetMonth: null,
  };

/**
 * Issue #453: ソート / グルーピング / 検索 / ページングを Mock UI で検証するための
 * 大量データ生成（project-014 に投入する）。
 *
 * 生成規則:
 *   - fileName: `<書類名>_<連番3桁>_sample.pdf`。書類名は 4 種を巡回させ、検索
 *     （部分一致・大文字小文字無視）とファイル名ソートの双方を確認できるようにする
 *   - attribution: 未分類 / employee / month / project の 4 パターンを巡回
 *     （case 紐付けは project-014 に Case が存在しないため使わない）
 *   - status: uploadStatus / ocrStatus / convertStatus を分散。7 件周期のうち 2 件を
 *     非終端にして、ポーリング継続と自動停止の両方を再現できるようにする
 *   - ocrFailedReason: OCR 失敗の 2 パターンに別々の理由を割り当て、一覧上で
 *     「利用上限」と「AI サーバー起因」の出し分けを確認できるようにする
 *     （理由不明 = null と変換失敗 = conversion_error は project-011 の固定データ側で
 *     用意している）
 *   - createdAt は連番昇順、uploadedAt は逆行するオフセットを与えて createdAt 順と
 *     交差させる（sortBy=uploadedAt が createdAt 順と別物であることを検証するため）
 */
function generateBulkEvidenceFiles(
  count: number,
): ProjectEvidenceFileItemApiResponse[] {
  const documentNames = ['雇用契約書', '給与明細', '出勤簿', '請求書'];
  const attributions = [
    EMPTY_ATTRIBUTION,
    EMPLOYEE_ATTRIBUTION_YAMADA,
    MONTH_ATTRIBUTION_APRIL,
    PROJECT_ATTRIBUTION_DISPATCH_CONTRACT,
  ];
  const statusPatterns: {
    uploadStatus: 'pending' | 'uploaded' | 'error';
    ocrStatus: 'pending' | 'processing' | 'completed' | 'failed';
    /** Issue #475 Phase 3: OCR 失敗以外の状態は理由を持たない（null） */
    ocrFailedReason: OcrFailedReason | null;
    convertStatus:
      | 'idle'
      | 'awaiting_conversion'
      | 'downloading'
      | 'completed'
      | 'failed'
      | null;
  }[] = [
    {
      uploadStatus: 'uploaded',
      ocrStatus: 'completed',
      ocrFailedReason: null,
      convertStatus: 'completed',
    },
    {
      uploadStatus: 'uploaded',
      ocrStatus: 'completed',
      ocrFailedReason: null,
      convertStatus: null,
    },
    // AI 利用上限に到達して OCR を実行できなかった（追加付与まで解消しない恒久エラー）
    {
      uploadStatus: 'uploaded',
      ocrStatus: 'failed',
      ocrFailedReason: 'usage_limit_exceeded',
      convertStatus: 'failed',
    },
    // AI サーバー起因の失敗（時間をおいた再実行で回復しうる）
    {
      uploadStatus: 'error',
      ocrStatus: 'failed',
      ocrFailedReason: 'ai_server_error',
      convertStatus: null,
    },
    {
      uploadStatus: 'uploaded',
      ocrStatus: 'completed',
      ocrFailedReason: null,
      convertStatus: 'failed',
    },
    // 非終端（ポーリング継続の確認用）
    {
      uploadStatus: 'pending',
      ocrStatus: 'pending',
      ocrFailedReason: null,
      convertStatus: 'idle',
    },
    {
      uploadStatus: 'uploaded',
      ocrStatus: 'processing',
      ocrFailedReason: null,
      convertStatus: 'awaiting_conversion',
    },
  ];

  const baseTime = Date.parse('2025-04-01T00:00:00.000Z');
  const hourMs = 60 * 60 * 1000;

  return Array.from({ length: count }, (_, i) => {
    const seq = String(i + 1).padStart(3, '0');
    const documentName = documentNames[i % documentNames.length];
    const pattern = statusPatterns[i % statusPatterns.length];
    const publicId = `evidence-file-p014-${seq}`;
    // createdAt は 1 時間刻みの昇順、uploadedAt は「末尾ほど古い」逆順オフセットを与える
    const createdAt = new Date(baseTime + i * hourMs).toISOString();
    const uploadedAt = new Date(
      baseTime + (count - i) * hourMs + (i % 5) * hourMs,
    ).toISOString();

    return {
      evidenceFile: {
        publicId,
        fileHash: `p014-bulk-${seq}`,
        fileName: `${documentName}_${seq}_sample.pdf`,
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt,
        updatedAt: createdAt,
        uploadedAt,
        uploadStatus: pattern.uploadStatus,
        ocrStatus: pattern.ocrStatus,
        ocrFailedReason: pattern.ocrFailedReason,
        attribution: attributions[i % attributions.length],
      },
      convertedArtifact:
        pattern.convertStatus === null
          ? null
          : {
              publicId: `converted-artifact-p014-${seq}`,
              fileHash: `conv-p014-bulk-${seq}`,
              fileName: `${documentName}_${seq}_sample_converted.pdf`,
              createdAt,
              status: pattern.convertStatus,
            },
      caseId: null,
    } satisfies ProjectEvidenceFileItemApiResponse;
  });
}

/**
 * エビデンスファイルMockデータ
 * プロジェクトIDに紐づくエビデンスファイル一覧
 */
export const MOCK_EVIDENCE_FILES: Record<
  string,
  ProjectEvidenceFileItemApiResponse[]
> = {
  // project-001 (直接雇用・正社員) のファイル
  'project-001': [
    // 山田 太郎 (case-001) のファイル
    {
      evidenceFile: {
        publicId: 'evidence-file-001',
        fileHash: 'a1b2c3d4e5f6...',
        fileName: '雇用契約書_山田太郎.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-10T10:05:00.000Z',
        updatedAt: '2025-01-10T10:05:00.000Z',
        uploadedAt: '2025-01-12T10:05:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-001',
        fileHash: 'conv-a1b2c3d4e5f6...',
        fileName: '雇用契約書_山田太郎_converted.pdf',
        createdAt: '2025-01-10T10:06:00.000Z',
        status: 'completed',
      },
      caseId: 'case-001',
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-002',
        fileHash: 'b2c3d4e5f6g7...',
        fileName: '社会保険資格取得届_山田太郎.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-10T10:06:00.000Z',
        updatedAt: '2025-01-10T10:06:00.000Z',
        uploadedAt: '2025-01-09T10:06:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'processing',
        ocrFailedReason: null,
        attribution: EMPLOYEE_ATTRIBUTION_YAMADA,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-002',
        fileHash: 'conv-b2c3d4e5f6g7...',
        fileName: '社会保険資格取得届_山田太郎_converted.pdf',
        createdAt: '2025-01-10T10:07:00.000Z',
        status: 'awaiting_conversion',
      },
      caseId: 'case-001',
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-003',
        fileHash: 'c3d4e5f6g7h8...',
        fileName: '雇用保険資格取得届_山田太郎.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-10T10:07:00.000Z',
        updatedAt: '2025-01-10T10:07:00.000Z',
        uploadedAt: '2025-01-10T11:07:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: MONTH_ATTRIBUTION_APRIL,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-003',
        fileHash: 'conv-c3d4e5f6g7h8...',
        fileName: '雇用保険資格取得届_山田太郎_converted.pdf',
        createdAt: '2025-01-10T10:08:00.000Z',
        status: 'completed',
      },
      caseId: 'case-001',
    },
    // 佐藤 花子 (case-002) のファイル
    {
      evidenceFile: {
        publicId: 'evidence-file-004',
        fileHash: 'd4e5f6g7h8i9...',
        fileName: '雇用契約書_佐藤花子.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-10T10:10:00.000Z',
        updatedAt: '2025-01-10T10:10:00.000Z',
        uploadedAt: '2025-01-07T10:10:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: CASE_ATTRIBUTION_SATO,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-004',
        fileHash: 'conv-d4e5f6g7h8i9...',
        fileName: '雇用契約書_佐藤花子_converted.pdf',
        createdAt: '2025-01-10T10:11:00.000Z',
        status: 'completed',
      },
      caseId: 'case-002',
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-005',
        fileHash: 'e5f6g7h8i9j0...',
        fileName: '社会保険資格取得届_佐藤花子.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-10T10:11:00.000Z',
        updatedAt: '2025-01-10T10:11:00.000Z',
        uploadedAt: '2025-01-10T22:11:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: PROJECT_ATTRIBUTION_ORG_CHART,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-005',
        fileHash: 'conv-e5f6g7h8i9j0...',
        fileName: '社会保険資格取得届_佐藤花子_converted.pdf',
        createdAt: '2025-01-10T10:12:00.000Z',
        status: 'completed',
      },
      caseId: 'case-002',
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-006',
        fileHash: 'f6g7h8i9j0k1...',
        fileName: '雇用保険資格取得届_佐藤花子.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-10T10:12:00.000Z',
        updatedAt: '2025-01-10T10:12:00.000Z',
        uploadedAt: '2025-01-10T08:12:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-006',
        fileHash: 'conv-f6g7h8i9j0k1...',
        fileName: '雇用保険資格取得届_佐藤花子_converted.pdf',
        createdAt: '2025-01-10T10:13:00.000Z',
        status: 'completed',
      },
      caseId: 'case-002',
    },
    // 鈴木 一郎 (case-003) のファイル
    {
      evidenceFile: {
        publicId: 'evidence-file-007',
        fileHash: 'g7h8i9j0k1l2...',
        fileName: '雇用契約書_鈴木一郎.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-10T10:15:00.000Z',
        updatedAt: '2025-01-10T10:15:00.000Z',
        uploadedAt: '2025-01-17T10:15:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-007',
        fileHash: 'conv-g7h8i9j0k1l2...',
        fileName: '雇用契約書_鈴木一郎_converted.pdf',
        createdAt: '2025-01-10T10:16:00.000Z',
        status: 'completed',
      },
      caseId: 'case-003',
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-008',
        fileHash: 'h8i9j0k1l2m3...',
        fileName: '社会保険資格取得届_鈴木一郎.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-10T10:16:00.000Z',
        updatedAt: '2025-01-10T10:16:00.000Z',
        uploadedAt: '2025-01-10T09:16:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-008',
        fileHash: 'conv-h8i9j0k1l2m3...',
        fileName: '社会保険資格取得届_鈴木一郎_converted.pdf',
        createdAt: '2025-01-10T10:17:00.000Z',
        status: 'completed',
      },
      caseId: 'case-003',
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-009',
        fileHash: 'i9j0k1l2m3n4...',
        fileName: '雇用保険資格取得届_鈴木一郎.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-10T10:17:00.000Z',
        updatedAt: '2025-01-10T10:17:00.000Z',
        uploadedAt: '2025-01-13T10:17:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-009',
        fileHash: 'conv-i9j0k1l2m3n4...',
        fileName: '雇用保険資格取得届_鈴木一郎_converted.pdf',
        createdAt: '2025-01-10T10:18:00.000Z',
        status: 'completed',
      },
      caseId: 'case-003',
    },
    // Issue #447 Phase B レビュー反映: matrix の allowedType 絞り込みを Mock UI で
    // 対話検証するため、case × employment / case × journal_entry の attribution を持つ
    // ファイルを 1 件ずつ追加する。
    {
      evidenceFile: {
        publicId: 'evidence-file-018',
        fileHash: 'x4y5z6a1b2c3...',
        fileName: '雇用審査ケース書類_山田太郎.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-10T10:20:00.000Z',
        updatedAt: '2025-01-10T10:20:00.000Z',
        uploadedAt: '2025-01-08T10:20:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: CASE_ATTRIBUTION_EMPLOYMENT_YAMADA,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-018',
        fileHash: 'conv-x4y5z6a1b2c3...',
        fileName: '雇用審査ケース書類_山田太郎_converted.pdf',
        createdAt: '2025-01-10T10:21:00.000Z',
        status: 'completed',
      },
      caseId: 'case-001',
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-019',
        fileHash: 'y5z6a1b2c3d4...',
        fileName: '仕訳審査ケース書類_山田太郎_202504.xlsx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-10T10:22:00.000Z',
        updatedAt: '2025-01-10T10:22:00.000Z',
        uploadedAt: '2025-01-12T10:22:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: CASE_ATTRIBUTION_JOURNAL_ENTRY_YAMADA,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-019',
        fileHash: 'conv-y5z6a1b2c3d4...',
        fileName: '仕訳審査ケース書類_山田太郎_202504_converted.pdf',
        createdAt: '2025-01-10T10:23:00.000Z',
        status: 'completed',
      },
      caseId: 'case-001-je-04',
    },
  ],

  // project-002 (直接雇用・契約社員（有期）) のファイル
  'project-002': [
    // 渡辺 裕子 (case-006) のファイル
    {
      evidenceFile: {
        publicId: 'evidence-file-010',
        fileHash: 'k1l2m3n4o5p6...',
        fileName: '雇用契約書_渡辺裕子.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-11T09:05:00.000Z',
        updatedAt: '2025-01-11T09:05:00.000Z',
        uploadedAt: '2025-01-10T09:05:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-010',
        fileHash: 'conv-k1l2m3n4o5p6...',
        fileName: '雇用契約書_渡辺裕子_converted.pdf',
        createdAt: '2025-01-11T09:06:00.000Z',
        status: 'completed',
      },
      caseId: 'case-006',
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-011',
        fileHash: 'l2m3n4o5p6q7...',
        fileName: '社会保険資格取得届_渡辺裕子.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-11T09:06:00.000Z',
        updatedAt: '2025-01-11T09:06:00.000Z',
        uploadedAt: '2025-01-11T10:06:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-011',
        fileHash: 'conv-l2m3n4o5p6q7...',
        fileName: '社会保険資格取得届_渡辺裕子_converted.pdf',
        createdAt: '2025-01-11T09:07:00.000Z',
        status: 'completed',
      },
      caseId: 'case-006',
    },
    // 中村 さくら (case-008) のファイル
    {
      evidenceFile: {
        publicId: 'evidence-file-012',
        fileHash: 'm3n4o5p6q7r8...',
        fileName: '雇用契約書_中村さくら.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-11T09:20:00.000Z',
        updatedAt: '2025-01-11T09:20:00.000Z',
        uploadedAt: '2025-01-08T09:20:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-012',
        fileHash: 'conv-m3n4o5p6q7r8...',
        fileName: '雇用契約書_中村さくら_converted.pdf',
        createdAt: '2025-01-11T09:21:00.000Z',
        status: 'completed',
      },
      caseId: 'case-008',
    },
    // 未紐付けファイル（pending 状態、ポーリング動作確認用）
    {
      evidenceFile: {
        publicId: 'evidence-file-013',
        fileHash: 'n4o5p6q7r8s9...',
        fileName: '契約書_名前不明.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-11T09:30:00.000Z',
        updatedAt: '2025-01-11T09:30:00.000Z',
        uploadedAt: '2025-01-11T21:30:00.000Z',
        uploadStatus: 'pending',
        ocrStatus: 'pending',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: null,
      caseId: null,
    },
  ],

  // project-003 (直接雇用・アルバイト) のファイル
  'project-003': [
    {
      evidenceFile: {
        publicId: 'evidence-file-014',
        fileHash: 'p6q7r8s9t0u1...',
        fileName: '雇用契約書_山本由美.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-12T08:05:00.000Z',
        updatedAt: '2025-01-12T08:05:00.000Z',
        uploadedAt: '2025-01-12T06:05:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-014',
        fileHash: 'conv-p6q7r8s9t0u1...',
        fileName: '雇用契約書_山本由美_converted.pdf',
        createdAt: '2025-01-12T08:06:00.000Z',
        status: 'completed',
      },
      caseId: 'case-012',
    },
  ],

  // project-004 (間接雇用・出向者) のファイル
  'project-004': [
    // 井上 剛 (case-014) のファイル
    {
      evidenceFile: {
        publicId: 'evidence-file-020',
        fileHash: 'p004-a1b2c3...',
        fileName: '雇用契約書_井上剛.docx',
        uploadedByUser: { publicId: 'user-002', email: 'admin@example.com' },
        createdAt: '2025-01-11T11:05:00.000Z',
        updatedAt: '2025-01-11T11:05:00.000Z',
        uploadedAt: '2025-01-18T11:05:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-020',
        fileHash: 'conv-p004-a1b2c3...',
        fileName: '雇用契約書_井上剛_converted.pdf',
        createdAt: '2025-01-11T11:06:00.000Z',
        status: 'completed',
      },
      caseId: 'case-014',
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-021',
        fileHash: 'p004-b2c3d4...',
        fileName: '派遣契約書_共通.docx',
        uploadedByUser: { publicId: 'user-002', email: 'admin@example.com' },
        createdAt: '2025-01-11T11:07:00.000Z',
        updatedAt: '2025-01-11T11:07:00.000Z',
        uploadedAt: '2025-01-11T10:07:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: PROJECT_ATTRIBUTION_DISPATCH_CONTRACT,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-021',
        fileHash: 'conv-p004-b2c3d4...',
        fileName: '派遣契約書_共通_converted.pdf',
        createdAt: '2025-01-11T11:08:00.000Z',
        status: 'completed',
      },
      caseId: null,
    },
    // 木村 愛 (case-015) のファイル
    {
      evidenceFile: {
        publicId: 'evidence-file-022',
        fileHash: 'p004-c3d4e5...',
        fileName: '雇用契約書_木村愛.docx',
        uploadedByUser: { publicId: 'user-002', email: 'admin@example.com' },
        createdAt: '2025-01-11T11:10:00.000Z',
        updatedAt: '2025-01-11T11:10:00.000Z',
        uploadedAt: '2025-01-14T11:10:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'processing',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-022',
        fileHash: 'conv-p004-c3d4e5...',
        fileName: '雇用契約書_木村愛_converted.pdf',
        createdAt: '2025-01-11T11:11:00.000Z',
        status: 'awaiting_conversion',
      },
      caseId: 'case-015',
    },
    // 未紐付けファイル
    {
      evidenceFile: {
        publicId: 'evidence-file-023',
        fileHash: 'p004-d4e5f6...',
        fileName: '添付書類_不明.docx',
        uploadedByUser: { publicId: 'user-002', email: 'admin@example.com' },
        createdAt: '2025-01-11T11:15:00.000Z',
        updatedAt: '2025-01-11T11:15:00.000Z',
        uploadedAt: '2025-01-09T11:15:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-023',
        fileHash: 'conv-p004-d4e5f6...',
        fileName: '添付書類_不明_converted.pdf',
        createdAt: '2025-01-11T11:16:00.000Z',
        status: 'completed',
      },
      caseId: null,
    },
  ],

  // project-005 (直接雇用・正社員 - 君津事業所) のファイル
  'project-005': [
    {
      evidenceFile: {
        publicId: 'evidence-file-015',
        fileHash: 'r8s9t0u1v2w3...',
        fileName: '雇用契約書_斎藤翔.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-13T10:05:00.000Z',
        updatedAt: '2025-01-13T10:05:00.000Z',
        uploadedAt: '2025-01-15T10:05:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-015',
        fileHash: 'conv-r8s9t0u1v2w3...',
        fileName: '雇用契約書_斎藤翔_converted.pdf',
        createdAt: '2025-01-13T10:06:00.000Z',
        status: 'completed',
      },
      caseId: 'case-016',
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-016',
        fileHash: 's9t0u1v2w3x4...',
        fileName: '社会保険資格取得届_斎藤翔.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-13T10:06:00.000Z',
        updatedAt: '2025-01-13T10:06:00.000Z',
        uploadedAt: '2025-01-12T10:06:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-016',
        fileHash: 'conv-s9t0u1v2w3x4...',
        fileName: '社会保険資格取得届_斎藤翔_converted.pdf',
        createdAt: '2025-01-13T10:07:00.000Z',
        status: 'completed',
      },
      caseId: 'case-016',
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-017',
        fileHash: 't0u1v2w3x4y5...',
        fileName: '雇用保険資格取得届_斎藤翔.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-13T10:07:00.000Z',
        updatedAt: '2025-01-13T10:07:00.000Z',
        uploadedAt: '2025-01-13T11:07:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-017',
        fileHash: 'conv-t0u1v2w3x4y5...',
        fileName: '雇用保険資格取得届_斎藤翔_converted.pdf',
        createdAt: '2025-01-13T10:08:00.000Z',
        status: 'completed',
      },
      caseId: 'case-016',
    },
  ],

  // project-006 (直接雇用・契約社員（無期） - 君津事業所) のファイル
  'project-006': [
    // 池田 智子 (case-019) のファイル
    {
      evidenceFile: {
        publicId: 'evidence-file-030',
        fileHash: 'p006-a1b2c3...',
        fileName: '雇用契約書_池田智子.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-09T14:05:00.000Z',
        updatedAt: '2025-01-09T14:05:00.000Z',
        uploadedAt: '2025-01-06T14:05:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: {
          documentTypeCode: 'employment_contract',
          documentTypePublicId: 'doc-type-001',
          attributionUnit: 'employee',
          caseId: null,
          employeeId: 'EMP019',
          targetMonth: null,
        },
      },
      convertedArtifact: {
        publicId: 'converted-artifact-030',
        fileHash: 'conv-p006-a1b2c3...',
        fileName: '雇用契約書_池田智子_converted.pdf',
        createdAt: '2025-01-09T14:06:00.000Z',
        status: 'completed',
      },
      caseId: 'case-019',
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-031',
        fileHash: 'p006-b2c3d4...',
        fileName: '社会保険資格取得届_池田智子.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-09T14:06:00.000Z',
        updatedAt: '2025-01-09T14:06:00.000Z',
        uploadedAt: '2025-01-10T02:06:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-031',
        fileHash: 'conv-p006-b2c3d4...',
        fileName: '社会保険資格取得届_池田智子_converted.pdf',
        createdAt: '2025-01-09T14:07:00.000Z',
        status: 'completed',
      },
      caseId: 'case-019',
    },
    // 未アップロード完了のファイル（ポーリング検証用）
    {
      evidenceFile: {
        publicId: 'evidence-file-032',
        fileHash: 'p006-c3d4e5...',
        fileName: '追加書類_処理中.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-09T14:10:00.000Z',
        updatedAt: '2025-01-09T14:10:00.000Z',
        uploadedAt: '2025-01-09T12:10:00.000Z',
        uploadStatus: 'pending',
        ocrStatus: 'pending',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: null,
      caseId: null,
    },
  ],

  // project-007 (GX事務局 A社 2025年度 直接雇用・正社員) のファイル
  'project-007': [
    {
      evidenceFile: {
        publicId: 'evidence-file-040',
        fileHash: 'p007-a1b2c3...',
        fileName: '組織図_A社.xlsx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-02-05T09:00:00.000Z',
        updatedAt: '2025-02-05T09:00:00.000Z',
        uploadedAt: '2025-02-12T09:00:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: PROJECT_ATTRIBUTION_ORG_CHART,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-040',
        fileHash: 'conv-p007-a1b2c3...',
        fileName: '組織図_A社_converted.pdf',
        createdAt: '2025-02-05T09:01:00.000Z',
        status: 'completed',
      },
      caseId: null,
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-041',
        fileHash: 'p007-b2c3d4...',
        fileName: '添付書類_A社_未分類.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-02-05T09:05:00.000Z',
        updatedAt: '2025-02-05T09:05:00.000Z',
        uploadedAt: '2025-02-05T08:05:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-041',
        fileHash: 'conv-p007-b2c3d4...',
        fileName: '添付書類_A社_未分類_converted.pdf',
        createdAt: '2025-02-05T09:06:00.000Z',
        status: 'completed',
      },
      caseId: null,
    },
  ],

  // project-008 (GX事務局 A社 2024年度 直接雇用・契約社員（有期）) のファイル
  'project-008': [
    {
      evidenceFile: {
        publicId: 'evidence-file-050',
        fileHash: 'p008-a1b2c3...',
        fileName: '組織図_A社_2024年度.xlsx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2024-05-10T09:00:00.000Z',
        updatedAt: '2024-05-10T09:00:00.000Z',
        uploadedAt: '2024-05-13T09:00:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: PROJECT_ATTRIBUTION_ORG_CHART,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-050',
        fileHash: 'conv-p008-a1b2c3...',
        fileName: '組織図_A社_2024年度_converted.pdf',
        createdAt: '2024-05-10T09:01:00.000Z',
        status: 'completed',
      },
      caseId: null,
    },
  ],

  // project-009 (GX事務局 B社 2025年度 直接雇用・アルバイト) のファイル
  'project-009': [
    {
      evidenceFile: {
        publicId: 'evidence-file-060',
        fileHash: 'p009-a1b2c3...',
        fileName: '組織図_B社.xlsx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-02-08T09:00:00.000Z',
        updatedAt: '2025-02-08T09:00:00.000Z',
        uploadedAt: '2025-02-06T09:00:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: PROJECT_ATTRIBUTION_ORG_CHART,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-060',
        fileHash: 'conv-p009-a1b2c3...',
        fileName: '組織図_B社_converted.pdf',
        createdAt: '2025-02-08T09:01:00.000Z',
        status: 'completed',
      },
      caseId: null,
    },
    {
      evidenceFile: {
        publicId: 'evidence-file-061',
        fileHash: 'p009-b2c3d4...',
        fileName: '未分類_B社_資料.xlsx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-02-08T09:10:00.000Z',
        updatedAt: '2025-02-08T09:10:00.000Z',
        uploadedAt: '2025-02-10T09:10:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-061',
        fileHash: 'conv-p009-b2c3d4...',
        fileName: '未分類_B社_資料_converted.pdf',
        createdAt: '2025-02-08T09:11:00.000Z',
        status: 'completed',
      },
      caseId: null,
    },
  ],

  // project-010 (中部製鉄所 名古屋事業所 直接雇用・正社員) のファイル
  'project-010': [
    {
      evidenceFile: {
        publicId: 'evidence-file-070',
        fileHash: 'p010-a1b2c3...',
        fileName: '組織図_名古屋事業所.xlsx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-13T10:05:00.000Z',
        updatedAt: '2025-01-13T10:05:00.000Z',
        uploadedAt: '2025-01-12T10:05:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: PROJECT_ATTRIBUTION_ORG_CHART,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-070',
        fileHash: 'conv-p010-a1b2c3...',
        fileName: '組織図_名古屋事業所_converted.pdf',
        createdAt: '2025-01-13T10:06:00.000Z',
        status: 'completed',
      },
      caseId: null,
    },
  ],

  // project-011 (西日本製鉄所 大阪事業所 直接雇用・契約社員（有期）) のファイル
  'project-011': [
    {
      evidenceFile: {
        publicId: 'evidence-file-080',
        fileHash: 'p011-a1b2c3...',
        fileName: '組織図_大阪事業所.xlsx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-12T10:05:00.000Z',
        updatedAt: '2025-01-12T10:05:00.000Z',
        uploadedAt: '2025-01-12T11:05:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: PROJECT_ATTRIBUTION_ORG_CHART,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-080',
        fileHash: 'conv-p011-a1b2c3...',
        fileName: '組織図_大阪事業所_converted.pdf',
        createdAt: '2025-01-12T10:06:00.000Z',
        status: 'completed',
      },
      caseId: null,
    },
    // OCR 失敗の状態を確認するためのファイル。
    // Issue #475 Phase 3 以前に失敗した既存行を模した「理由不明（null）」のケース。
    // 遡って理由を埋められないため、viewer は上限到達に寄せず中立に表示する。
    {
      evidenceFile: {
        publicId: 'evidence-file-081',
        fileHash: 'p011-b2c3d4...',
        fileName: 'OCR失敗サンプル.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-01-12T10:10:00.000Z',
        updatedAt: '2025-01-12T10:10:00.000Z',
        uploadedAt: '2025-01-09T10:10:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'failed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-081',
        fileHash: 'conv-p011-b2c3d4...',
        fileName: 'OCR失敗サンプル_converted.pdf',
        createdAt: '2025-01-12T10:11:00.000Z',
        status: 'failed',
      },
      caseId: null,
    },
    // Issue #475 Phase 3/5: AI 利用上限に到達して OCR を実行できなかったケース。
    // 追加付与まで解消しない恒久エラーなので、viewer はリトライを示唆しない表示にする。
    {
      evidenceFile: {
        publicId: 'evidence-file-082',
        fileHash: 'p011-c3d4e5...',
        fileName: 'OCR失敗_利用上限サンプル.pdf',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2026-08-20T04:00:00.000Z',
        updatedAt: '2026-08-20T04:02:00.000Z',
        uploadedAt: '2026-08-20T04:00:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'failed',
        ocrFailedReason: 'usage_limit_exceeded',
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: null,
      caseId: null,
    },
    // Issue #475 Phase 3/5: AI サーバー起因の失敗。時間をおいた再実行で回復しうる。
    {
      evidenceFile: {
        publicId: 'evidence-file-083',
        fileHash: 'p011-d4e5f6...',
        fileName: 'OCR失敗_AIサーバーエラーサンプル.pdf',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2026-08-20T04:10:00.000Z',
        updatedAt: '2026-08-20T04:12:00.000Z',
        uploadedAt: '2026-08-20T04:10:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'failed',
        ocrFailedReason: 'ai_server_error',
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: null,
      caseId: null,
    },
    // Issue #600: 証憑ファイルの PDF 変換失敗（ConvertToPreviewFailed webhook）。
    // 修正前は到達不能な UploadError 経路へ dispatch していたため OcrProcessing の
    // まま固まっていた。修正後は総括表（Issue #550）と同じ失敗系に合流し、
    // OcrProcessing → OcrError + conversion_error で終端する。
    // S3 へのアップロード自体は成功しているので uploadStatus は 'uploaded'
    // （旧仕様の 'error' ではない）。
    // convertedArtifact は上の 2 件（利用上限 / AI エラー）と揃えて null にする。
    // 変換に成功していないので成果物は無く、実 API にも ConvertedArtifact を
    // 'failed' へ落とす経路は無い（変換失敗は EvidenceFile 側の理由で表す）。
    // なお実 API では ConvertedArtifact 行自体は EvidenceFile 作成時に idle で
    // 作られるため、null は「行が無い旧データ」に相当する。idle のままだと
    // isFileProcessingTerminal が非終端と判定してポーリングが止まらないので、
    // 失敗理由の見本としては終端状態（null）で置く。
    {
      evidenceFile: {
        publicId: 'evidence-file-084',
        fileHash: 'p011-e5f6a7...',
        fileName: 'OCR失敗_変換エラーサンプル.xlsx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2026-09-18T04:20:00.000Z',
        updatedAt: '2026-09-18T04:22:00.000Z',
        uploadedAt: '2026-09-18T04:20:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'failed',
        ocrFailedReason: 'conversion_error',
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: null,
      caseId: null,
    },
  ],

  // project-012 (間接雇用・派遣社員 - idle 確認用) のファイル
  'project-012': [
    {
      evidenceFile: {
        publicId: 'evidence-file-090',
        fileHash: 'p012-a1b2c3...',
        fileName: '派遣契約書_サンプル.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-03-05T09:00:00.000Z',
        updatedAt: '2025-03-05T09:00:00.000Z',
        uploadedAt: '2025-03-05T21:00:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: PROJECT_ATTRIBUTION_DISPATCH_CONTRACT,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-090',
        fileHash: 'conv-p012-a1b2c3...',
        fileName: '派遣契約書_サンプル_converted.pdf',
        createdAt: '2025-03-05T09:01:00.000Z',
        status: 'completed',
      },
      caseId: null,
    },
  ],

  // project-013 (間接雇用・業務委託 - failed 確認用) のファイル
  'project-013': [
    {
      evidenceFile: {
        publicId: 'evidence-file-100',
        fileHash: 'p013-a1b2c3...',
        fileName: '業務委託契約書_サンプル.docx',
        uploadedByUser: { publicId: 'user-001', email: 'user@example.com' },
        createdAt: '2025-03-10T09:00:00.000Z',
        updatedAt: '2025-03-10T09:00:00.000Z',
        uploadedAt: '2025-03-10T07:00:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-100',
        fileHash: 'conv-p013-a1b2c3...',
        fileName: '業務委託契約書_サンプル_converted.pdf',
        createdAt: '2025-03-10T09:01:00.000Z',
        status: 'completed',
      },
      caseId: null,
    },
  ],

  // project-014 (直接雇用・嘱託 - cancelled 確認用) のファイル
  'project-014': [
    {
      evidenceFile: {
        publicId: 'evidence-file-110',
        fileHash: 'p014-a1b2c3...',
        fileName: '嘱託契約書_サンプル.docx',
        uploadedByUser: { publicId: 'user-002', email: 'admin@example.com' },
        createdAt: '2025-03-15T09:00:00.000Z',
        updatedAt: '2025-03-15T09:00:00.000Z',
        uploadedAt: '2025-03-22T09:00:00.000Z',
        uploadStatus: 'uploaded',
        ocrStatus: 'completed',
        ocrFailedReason: null,
        attribution: EMPTY_ATTRIBUTION,
      },
      convertedArtifact: {
        publicId: 'converted-artifact-110',
        fileHash: 'conv-p014-a1b2c3...',
        fileName: '嘱託契約書_サンプル_converted.pdf',
        createdAt: '2025-03-15T09:01:00.000Z',
        status: 'completed',
      },
      caseId: null,
    },
    // Issue #453: ページング / ソート / 検索の検証用に 120 件を追加（合計 121 件）
    ...generateBulkEvidenceFiles(120),
  ],
};

/**
 * プロジェクトIDからエビデンスファイル一覧を取得
 */
export function findEvidenceFilesByProjectId(
  projectId: string,
): ProjectEvidenceFileItemApiResponse[] {
  return MOCK_EVIDENCE_FILES[projectId] ?? [];
}

/**
 * エビデンスファイルIDからファイルを取得
 */
export function findEvidenceFileById(
  projectId: string,
  evidenceFileId: string,
): ProjectEvidenceFileItemApiResponse | undefined {
  const files = findEvidenceFilesByProjectId(projectId);
  return files.find((item) => item.evidenceFile.publicId === evidenceFileId);
}

/**
 * 審査対象IDに紐付くエビデンスファイルを取得
 */
export function findEvidenceFilesByCaseId(
  projectId: string,
  caseId: string,
): ProjectEvidenceFileItemApiResponse[] {
  const files = findEvidenceFilesByProjectId(projectId);
  return files.filter((item) => item.caseId === caseId);
}

/**
 * エビデンスファイルを削除（Mock用）
 */
export function deleteEvidenceFile(
  projectId: string,
  evidenceFileId: string,
): boolean {
  const files = MOCK_EVIDENCE_FILES[projectId] as
    | ProjectEvidenceFileItemApiResponse[]
    | undefined;
  if (!files) return false;

  const index = files.findIndex(
    (item) => item.evidenceFile.publicId === evidenceFileId,
  );
  if (index === -1) return false;

  files.splice(index, 1);
  return true;
}

/**
 * fileName と fileHash の両方が一致するエビデンスファイルを検索（重複チェック用）
 * api-server.openapi.json の重複判定条件: fileName と fileHash の両方が一致する場合のみ重複とみなす
 */
export function findEvidenceFileByNameAndHash(
  projectId: string,
  fileName: string,
  fileHash: string,
): ProjectEvidenceFileItemApiResponse | undefined {
  const files = findEvidenceFilesByProjectId(projectId);
  return files.find(
    (item) =>
      item.evidenceFile.fileName === fileName &&
      item.evidenceFile.fileHash === fileHash,
  );
}

/**
 * エビデンスファイルを追加（Mock用）
 */
export function addEvidenceFile(
  projectId: string,
  file: ProjectEvidenceFileItemApiResponse,
): void {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- 動的に追加されるキーのため
  if (!MOCK_EVIDENCE_FILES[projectId]) {
    MOCK_EVIDENCE_FILES[projectId] = [];
  }
  MOCK_EVIDENCE_FILES[projectId].push(file);
}

/**
 * エビデンスファイルの attribution を上書き更新する（Mock 用、Issue #443/#447）。
 * updatedAt は現在時刻に更新し、更新後の項目を返す。存在しない場合は undefined。
 */
export function updateEvidenceFileAttribution(
  projectId: string,
  evidenceFileId: string,
  attribution: EvidenceFileAttributionApiResponse,
): ProjectEvidenceFileItemApiResponse | undefined {
  const files = MOCK_EVIDENCE_FILES[projectId] as
    | ProjectEvidenceFileItemApiResponse[]
    | undefined;
  if (!files) return undefined;

  const index = files.findIndex(
    (item) => item.evidenceFile.publicId === evidenceFileId,
  );
  if (index === -1) return undefined;

  const current = files[index];
  const updated: ProjectEvidenceFileItemApiResponse = {
    ...current,
    evidenceFile: {
      ...current.evidenceFile,
      attribution,
      updatedAt: new Date().toISOString(),
    },
  };
  files[index] = updated;
  return updated;
}
